// A ZIP reader small enough to justify not adding a dependency.
//
// The planner needs exactly one thing out of a .docx: the bytes of
// `word/document.xml`. A .docx is a ZIP archive whose entries are either
// stored or raw-deflated, and every browser this app targets ships
// DecompressionStream('deflate-raw'), so the whole job is parsing the central
// directory and handing one entry to the platform's inflater.
//
// JSZip would do this too, but it is a devDependency used only by tests —
// promoting it would put ~100 KB into the app bundle to read one XML file.

import { inflate } from './inflate';

const EOCD_SIGNATURE = 0x06054b50;
const CENTRAL_FILE_SIGNATURE = 0x02014b50;
const LOCAL_FILE_SIGNATURE = 0x04034b50;
const ZIP64_EOCD_LOCATOR_SIGNATURE = 0x07064b50;

const STORED = 0;
const DEFLATED = 8;

/**
 * Find the end-of-central-directory record, scanning backwards from the end.
 * The record is 22 bytes plus a comment of up to 64 KB, so that is how far
 * back it is worth looking.
 */
const findEndOfCentralDirectory = (view, length) => {
  const earliest = Math.max(0, length - 22 - 0xffff);
  for (let offset = length - 22; offset >= earliest; offset -= 1) {
    if (view.getUint32(offset, true) === EOCD_SIGNATURE) return offset;
  }
  return -1;
};

/**
 * List every entry in the archive as
 * `{ name, method, compressedSize, uncompressedSize, localHeaderOffset }`.
 */
export const readZipDirectory = (buffer) => {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const eocd = findEndOfCentralDirectory(view, bytes.byteLength);
  if (eocd < 0) throw new Error('Not a ZIP archive (no end-of-central-directory record).');

  // Zip64 archives store the real offsets in a separate record. A .docx that
  // large is not something to guess at, so say so rather than misread it.
  if (eocd >= 20 && view.getUint32(eocd - 20, true) === ZIP64_EOCD_LOCATOR_SIGNATURE) {
    throw new Error('Zip64 archives are not supported.');
  }

  const entryCount = view.getUint16(eocd + 10, true);
  let offset = view.getUint32(eocd + 16, true);
  const entries = [];

  for (let index = 0; index < entryCount; index += 1) {
    if (offset + 46 > bytes.byteLength || view.getUint32(offset, true) !== CENTRAL_FILE_SIGNATURE) break;
    const nameLength = view.getUint16(offset + 28, true);
    const extraLength = view.getUint16(offset + 30, true);
    const commentLength = view.getUint16(offset + 32, true);
    entries.push({
      name: new TextDecoder('utf-8').decode(bytes.subarray(offset + 46, offset + 46 + nameLength)),
      method: view.getUint16(offset + 10, true),
      compressedSize: view.getUint32(offset + 20, true),
      uncompressedSize: view.getUint32(offset + 24, true),
      localHeaderOffset: view.getUint32(offset + 42, true)
    });
    offset += 46 + nameLength + extraLength + commentLength;
  }

  return entries;
};

/**
 * Read one entry out of a ZIP archive and decode it as UTF-8 text.
 *
 * @param {ArrayBuffer|Uint8Array} buffer the whole archive
 * @param {string} entryName exact path inside the archive, e.g. 'word/document.xml'
 * @returns {Promise<string|null>} the entry's text, or null if it is not there
 */
export const readZipEntryText = async (buffer, entryName) => {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  const entry = readZipDirectory(bytes).find((candidate) => candidate.name === entryName);
  if (!entry) return null;

  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const header = entry.localHeaderOffset;
  if (view.getUint32(header, true) !== LOCAL_FILE_SIGNATURE) {
    throw new Error(`Damaged ZIP entry: ${entryName}`);
  }
  // The local header repeats the name and extra-field lengths, and its extra
  // field is often a different length from the central directory's — so the
  // data offset has to come from here, not from the directory entry.
  const nameLength = view.getUint16(header + 26, true);
  const extraLength = view.getUint16(header + 28, true);
  const dataStart = header + 30 + nameLength + extraLength;
  const data = bytes.subarray(dataStart, dataStart + entry.compressedSize);

  if (entry.method === STORED) return new TextDecoder('utf-8').decode(data);
  if (entry.method === DEFLATED) return new TextDecoder('utf-8').decode(await inflate(data, 'deflate-raw'));
  throw new Error(`Unsupported ZIP compression method: ${entry.method}`);
};

export const __testing = { findEndOfCentralDirectory };
